import {
  useEffect,
  useState,
  type ReactNode,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { ApiError } from '../../api/client.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  getBranding,
  updateBranding,
  uploadBrandingAsset,
  BRANDING_ASSETS,
  type BrandingConfig,
  type BrandingUpdateInput,
} from '../../api/brandingApi.js';
import { fileToBase64 } from '../../../../shared/contracts/file-bytes.js';

/** M01-§7 white-label editor — all 28 fields grouped, plus §16 asset uploads. */
type FieldKey = keyof BrandingConfig;
type FieldKind = 'text' | 'color' | 'email' | 'url' | 'textarea';
interface FieldDef {
  readonly key: FieldKey;
  readonly label: string;
  readonly kind?: FieldKind;
  readonly required?: boolean;
}

const GROUPS: readonly { title: string; fields: readonly FieldDef[] }[] = [
  {
    title: 'Identity',
    fields: [
      { key: 'applicationName', label: 'App title', required: true },
      { key: 'shortName', label: 'Short name' },
      { key: 'tagline', label: 'Tagline' },
      { key: 'website', label: 'Company website', kind: 'url' },
    ],
  },
  {
    title: 'Contact',
    fields: [
      { key: 'supportEmail', label: 'Support email', kind: 'email' },
      { key: 'supportMobile', label: 'Support mobile' },
    ],
  },
  {
    title: 'Colors',
    fields: [
      { key: 'primaryColor', label: 'Primary color', kind: 'color' },
      { key: 'secondaryColor', label: 'Secondary color', kind: 'color' },
      { key: 'accentColor', label: 'Accent color', kind: 'color' },
    ],
  },
  {
    title: 'App & SEO',
    fields: [
      { key: 'androidAppName', label: 'Android app name' },
      { key: 'iosAppName', label: 'iOS app name' },
      { key: 'browserTitle', label: 'Browser title' },
      { key: 'metaDescription', label: 'Meta description', kind: 'textarea' },
      { key: 'metaKeywords', label: 'Meta keywords' },
    ],
  },
  {
    title: 'Legal',
    fields: [
      { key: 'copyrightText', label: 'Copyright text' },
      { key: 'termsUrl', label: 'Terms URL', kind: 'url' },
      { key: 'privacyUrl', label: 'Privacy URL', kind: 'url' },
    ],
  },
];

const FIELD_KEYS: readonly string[] = GROUPS.flatMap((g) =>
  g.fields.map((f) => f.key as string),
);
const ASSET_KEYS: readonly string[] = BRANDING_ASSETS.map((a) => a.field);
const ALL_STRING_KEYS: readonly string[] = [...FIELD_KEYS, ...ASSET_KEYS];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/.+/i;
const COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export function BrandingPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [config, setConfig] = useState<BrandingConfig | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [version, setVersion] = useState<number | undefined>(undefined);
  const [themeId, setThemeId] = useState<string | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [assetStatus, setAssetStatus] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);
  const [contrastWarn, setContrastWarn] = useState(false);
  const [ackContrast, setAckContrast] = useState(false);

  useEffect(() => {
    let alive = true;
    getBranding(token)
      .then((cfg) => {
        if (!alive) return;
        const base: BrandingConfig = cfg ?? {};
        setConfig(base);
        const next: Record<string, string> = {};
        for (const k of ALL_STRING_KEYS) {
          const v = (base as Record<string, unknown>)[k];
          next[k] = v == null ? '' : String(v);
        }
        setForm(next);
        setVersion(base.version);
        setThemeId(base.themeId);
      })
      .catch((e) => {
        if (alive) setLoadError((e as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const set = (k: string, val: string) => {
    setForm((f) => ({ ...f, [k]: val }));
    setSaveOk(false);
  };

  function validate(): boolean {
    const fe: Record<string, string> = {};
    if (!form.applicationName || !form.applicationName.trim()) {
      fe.applicationName = t('branding.err.appTitleRequired');
    }
    if (form.supportEmail && !EMAIL_RE.test(form.supportEmail)) {
      fe.supportEmail = t('branding.err.validEmail');
    }
    for (const k of ['website', 'termsUrl', 'privacyUrl']) {
      if (form[k] && !URL_RE.test(form[k])) {
        fe[k] = t('branding.err.validUrl');
      }
    }
    for (const k of ['primaryColor', 'secondaryColor', 'accentColor']) {
      if (form[k] && !COLOR_RE.test(form[k])) {
        fe[k] = t('branding.err.validHex');
      }
    }
    setFieldErrors(fe);
    return Object.keys(fe).length === 0;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaveError(null);
    setSaveOk(false);
    if (!validate()) return;
    const draft: Record<string, unknown> = {};
    for (const k of ALL_STRING_KEYS) {
      draft[k] = form[k] ?? '';
    }
    if (version !== undefined) draft.version = version;
    if (themeId) draft.themeId = themeId;
    if (ackContrast) draft.contrastAcknowledgedAt = new Date().toISOString();
    const body: BrandingUpdateInput = draft;
    setSaving(true);
    try {
      const saved = await updateBranding(body, token);
      setConfig(saved);
      setVersion(saved.version);
      setContrastWarn(false);
      setAckContrast(false);
      setSaveOk(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setContrastWarn(true);
        setSaveError(
          (err.message || t('branding.err.contrastDefault')) +
            ' ' + t('branding.err.acknowledgeOverride'),
        );
      } else {
        setSaveError((err as Error).message);
      }
    } finally {
      setSaving(false);
    }
  }

  async function onPickFile(
    assetType: string,
    field: string,
    e: ChangeEvent<HTMLInputElement>,
  ) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAssetStatus((s) => ({ ...s, [assetType]: t('branding.asset.uploading') }));
    try {
      const base64 = await fileToBase64(file);
      const res = await uploadBrandingAsset(assetType, base64, file.type, token);
      setForm((f) => ({ ...f, [field]: res.objectKey }));
      setAssetStatus((s) => ({ ...s, [assetType]: t('branding.asset.uploaded') }));
    } catch (err) {
      setAssetStatus((s) => ({ ...s, [assetType]: (err as Error).message }));
    }
  }

  if (loadError) return <p role="alert">{loadError}</p>;
  if (!config) return <p>{t('branding.loading')}</p>;

  const renderField = (f: FieldDef): ReactNode => {
    const id = `bf-${String(f.key)}`;
    const value = form[f.key as string] ?? '';
    const err = fieldErrors[f.key as string];
    const onChange = (
      ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => set(f.key as string, ev.target.value);
    return (
      <div key={String(f.key)} className="branding-field">
        <label htmlFor={id}>
          {t(`branding.field.${String(f.key)}`)}
          {f.required ? ' *' : ''}
        </label>
        {f.kind === 'textarea' ? (
          <textarea
            id={id}
            value={value}
            onChange={onChange}
            data-testid={`field-${String(f.key)}`}
          />
        ) : (
          <input
            id={id}
            type={f.kind === 'color' ? 'color' : f.kind === 'email' ? 'email' : 'text'}
            value={value}
            onChange={onChange}
            data-testid={`field-${String(f.key)}`}
          />
        )}
        {err ? (
          <span className="field-error" data-testid={`err-${String(f.key)}`}>
            {err}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <main aria-label="branding-editor">
      <h1>{t('branding.title')}</h1>
      <form aria-label="branding-form" noValidate onSubmit={onSubmit}>
        {GROUPS.map((g) => (
          <fieldset key={g.title} data-testid={`group-${g.title}`}>
            <legend>{t(`branding.group.${g.title}`)}</legend>
            {g.fields.map(renderField)}
          </fieldset>
        ))}

        <fieldset data-testid="group-Assets">
          <legend>{t('branding.group.Assets')}</legend>
          {BRANDING_ASSETS.map((a) => {
            const fileId = `bupload-${a.assetType}`;
            return (
              <div
                key={a.assetType}
                className="branding-asset"
                data-testid={`asset-${a.assetType}`}
              >
                <label htmlFor={`bkey-${a.assetType}`}>{t(`branding.asset.${a.assetType}`)} {t('branding.asset.keySuffix')}</label>
                <input
                  id={`bkey-${a.assetType}`}
                  value={form[a.field] ?? ''}
                  readOnly
                  data-testid={`assetkey-${a.assetType}`}
                />
                <label htmlFor={fileId}>{t(`branding.asset.${a.assetType}`)} {t('branding.asset.fileSuffix')}</label>
                <input
                  id={fileId}
                  type="file"
                  data-testid={`upload-${a.assetType}`}
                  onChange={(e) => onPickFile(a.assetType, a.field, e)}
                />
                {assetStatus[a.assetType] ? (
                  <span
                    role="status"
                    data-testid={`assetstatus-${a.assetType}`}
                  >
                    {assetStatus[a.assetType]}
                  </span>
                ) : null}
              </div>
            );
          })}
        </fieldset>

        {contrastWarn ? (
          <label data-testid="ack-contrast">
            <input
              type="checkbox"
              checked={ackContrast}
              onChange={(e) => setAckContrast(e.target.checked)}
            />
            {t('branding.ackContrast')}
          </label>
        ) : null}

        <button type="submit" disabled={saving} data-testid="save-branding">
          {saving ? t('branding.saving') : t('branding.save')}
        </button>
      </form>

      {saveOk ? <p role="status">{t('branding.saved')}</p> : null}
      {saveError ? <p role="alert">{saveError}</p> : null}
    </main>
  );
}
